import React, { useState, useRef, useMemo } from 'react';
import { X, Printer, Download, FileText } from 'lucide-react';

const ActivityReportModal = ({ 
  isOpen, 
  onClose, 
  activities, 
  enrollments, 
  categories,
  selectedYear 
}) => {
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showOnlyWithBalance, setShowOnlyWithBalance] = useState(false);
  const [includeFinancials, setIncludeFinancials] = useState(true);
  const reportRef = useRef(null);

  // Filter activities by category
  const filteredActivities = useMemo(() => {
    if (selectedCategory === 'all') return activities;
    return activities.filter(a => a.CategoryID === parseInt(selectedCategory));
  }, [activities, selectedCategory]);

  // Get filtered enrollments
  const filteredEnrollments = useMemo(() => {
    let result = enrollments.filter(e => e.Status === 'Active');
    
    if (selectedCategory !== 'all') {
      result = result.filter(e => e.CategoryID === parseInt(selectedCategory));
    }
    
    if (selectedActivity !== 'all') {
      result = result.filter(e => e.ActivityID === parseInt(selectedActivity));
    }
    
    if (showOnlyWithBalance) {
      result = result.filter(e => (e.BalanceRemaining || 0) > 0);
    }
    
    return result;
  }, [enrollments, selectedCategory, selectedActivity, showOnlyWithBalance]);

  // Group enrollments by activity
  const enrollmentsByActivity = useMemo(() => {
    const grouped = {};
    filteredEnrollments.forEach(enrollment => {
      const actId = enrollment.ActivityID;
      if (!grouped[actId]) {
        grouped[actId] = {
          activity: activities.find(a => a.ActivityID === actId),
          enrollments: []
        };
      }
      grouped[actId].enrollments.push(enrollment);
    });
    
    // Sort enrollments within each activity by student name
    Object.values(grouped).forEach(group => {
      group.enrollments.sort((a, b) => 
        `${a.LastName} ${a.FirstName}`.localeCompare(`${b.LastName} ${b.FirstName}`)
      );
    });
    
    return Object.values(grouped).sort((a, b) => 
      a.activity?.ActivityName?.localeCompare(b.activity?.ActivityName)
    );
  }, [filteredEnrollments, activities]);

  // Calculate totals
  const totals = useMemo(() => {
    return {
      students: filteredEnrollments.length,
      revenue: filteredEnrollments.reduce((sum, e) => sum + (e.FinalCost || 0), 0),
      collected: filteredEnrollments.reduce((sum, e) => sum + (e.AmountPaid || 0), 0),
      outstanding: filteredEnrollments.reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0)
    };
  }, [filteredEnrollments]);

  // Get report title
  const getReportTitle = () => {
    if (selectedActivity !== 'all') {
      const activity = activities.find(a => a.ActivityID === parseInt(selectedActivity));
      return `${activity?.ActivityName || 'Activity'} - Student List`;
    }
    if (selectedCategory !== 'all') {
      const category = categories.find(c => c.CategoryID === parseInt(selectedCategory));
      return `${category?.CategoryName || 'Category'} - Student List`;
    }
    return 'All Activities - Student List';
  };

  // Print report
  const handlePrint = () => {
    const printContent = reportRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${getReportTitle()}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px; 
            line-height: 1.4;
            padding: 20px;
          }
          .report-header { 
            text-align: center; 
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 2px solid #333;
          }
          .report-header h1 { 
            font-size: 18px; 
            margin-bottom: 5px; 
          }
          .report-header p { 
            font-size: 12px; 
            color: #666; 
          }
          .summary-box {
            display: flex;
            justify-content: space-between;
            background: #f5f5f5;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
          }
          .summary-item { text-align: center; }
          .summary-item .label { font-size: 10px; color: #666; }
          .summary-item .value { font-size: 14px; font-weight: bold; }
          .activity-section { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          .activity-header { 
            background: #e0e0e0; 
            padding: 8px 12px; 
            font-weight: bold;
            font-size: 12px;
            display: flex;
            justify-content: space-between;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 5px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px 8px; 
            text-align: left; 
          }
          th { 
            background: #f0f0f0; 
            font-size: 10px;
            text-transform: uppercase;
          }
          td { font-size: 11px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-bold { font-weight: bold; }
          .text-green { color: #16a34a; }
          .text-red { color: #dc2626; }
          .totals-row { background: #f5f5f5; font-weight: bold; }
          .footer { 
            margin-top: 30px; 
            padding-top: 15px; 
            border-top: 1px solid #ddd;
            font-size: 10px;
            color: #666;
            display: flex;
            justify-content: space-between;
          }
          .class-badge {
            background: #e5e7eb;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 10px;
          }
          @media print {
            body { padding: 0; }
            .activity-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Activity', 'Category', 'Student Name', 'Class', 'Father Name'];
    if (includeFinancials) {
      headers.push('Cost', 'Paid', 'Balance');
    }
    
    const rows = filteredEnrollments.map(e => {
      const row = [
        e.ActivityName,
        e.CategoryName,
        `${e.FirstName} ${e.LastName}`,
        e.Grade || '',
        e.FatherName || ''
      ];
      if (includeFinancials) {
        row.push(
          e.FinalCost?.toFixed(2) || '0.00',
          e.AmountPaid?.toFixed(2) || '0.00',
          e.BalanceRemaining?.toFixed(2) || '0.00'
        );
      }
      return row;
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity_report_${selectedYear}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold">Activity Reports</h2>
              <p className="text-sm text-gray-600">Generate and print enrollment reports</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedActivity('all');
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.CategoryID} value={cat.CategoryID}>
                    {cat.CategoryName}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
              <select
                value={selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Activities</option>
                {filteredActivities.map(act => (
                  <option key={act.ActivityID} value={act.ActivityID}>
                    {act.ActivityName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col justify-end">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyWithBalance}
                  onChange={(e) => setShowOnlyWithBalance(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-600">Only with balance due</span>
              </label>
              <label className="inline-flex items-center cursor-pointer mt-2">
                <input
                  type="checkbox"
                  checked={includeFinancials}
                  onChange={(e) => setIncludeFinancials(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-600">Include financial data</span>
              </label>
            </div>
            
            <div className="flex items-end gap-2">
              <button
                onClick={handlePrint}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                title="Export to CSV"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Report Preview */}
        <div className="flex-1 overflow-auto p-6 bg-gray-100">
          <div 
            ref={reportRef}
            className="bg-white shadow-lg mx-auto max-w-4xl p-8"
            style={{ minHeight: '11in' }}
          >
            {/* Report Header */}
            <div className="report-header text-center mb-6 pb-4 border-b-2 border-gray-800">
              <h1 className="text-xl font-bold mb-1">Collège des Apôtres</h1>
              <p className="text-gray-600 text-sm mb-2">Activities Enrollment Report</p>
              <h2 className="text-lg font-semibold">{getReportTitle()}</h2>
              <p className="text-gray-500 text-sm">Academic Year: {selectedYear}</p>
            </div>

            {/* Summary Box */}
            <div className="summary-box flex justify-between bg-gray-100 p-4 mb-6 rounded">
              <div className="summary-item text-center">
                <div className="label text-xs text-gray-500 uppercase">Total Students</div>
                <div className="value text-xl font-bold">{totals.students}</div>
              </div>
              {includeFinancials && (
                <>
                  <div className="summary-item text-center">
                    <div className="label text-xs text-gray-500 uppercase">Total Revenue</div>
                    <div className="value text-xl font-bold">${totals.revenue.toFixed(2)}</div>
                  </div>
                  <div className="summary-item text-center">
                    <div className="label text-xs text-gray-500 uppercase">Collected</div>
                    <div className="value text-xl font-bold text-green-600">${totals.collected.toFixed(2)}</div>
                  </div>
                  <div className="summary-item text-center">
                    <div className="label text-xs text-gray-500 uppercase">Outstanding</div>
                    <div className="value text-xl font-bold text-red-600">${totals.outstanding.toFixed(2)}</div>
                  </div>
                </>
              )}
            </div>

            {/* Activities and Students */}
            {enrollmentsByActivity.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                No enrollments found for the selected filters
              </div>
            ) : (
              enrollmentsByActivity.map(({ activity, enrollments: actEnrollments }) => (
                <div key={activity?.ActivityID} className="activity-section mb-6">
                  {/* Activity Header */}
                  <div className="activity-header bg-gray-200 px-3 py-2 flex justify-between items-center">
                    <div>
                      <span className="font-semibold">{activity?.ActivityName}</span>
                      <span className="text-gray-600 text-sm ml-2">
                        ({activity?.CategoryName} • {activity?.PaymentSchedule})
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="font-semibold">{actEnrollments.length}</span> students
                      {includeFinancials && (
                        <span className="ml-3 text-red-600">
                          ${actEnrollments.reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0).toFixed(2)} due
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Student Table */}
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border px-2 py-1 text-left w-8">#</th>
                        <th className="border px-2 py-1 text-left">Student Name</th>
                        <th className="border px-2 py-1 text-left w-20">Class</th>
                        <th className="border px-2 py-1 text-left">Father Name</th>
                        {includeFinancials && (
                          <>
                            <th className="border px-2 py-1 text-right w-20">Cost</th>
                            <th className="border px-2 py-1 text-right w-20">Paid</th>
                            <th className="border px-2 py-1 text-right w-20">Balance</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {actEnrollments.map((enrollment, index) => (
                        <tr key={enrollment.EnrollmentID} className="hover:bg-gray-50">
                          <td className="border px-2 py-1 text-gray-500">{index + 1}</td>
                          <td className="border px-2 py-1 font-medium">
                            {enrollment.FirstName} {enrollment.LastName}
                          </td>
                          <td className="border px-2 py-1">
                            <span className="class-badge bg-gray-100 px-2 py-0.5 rounded text-xs">
                              {enrollment.Grade}
                            </span>
                          </td>
                          <td className="border px-2 py-1 text-gray-600">{enrollment.FatherName}</td>
                          {includeFinancials && (
                            <>
                              <td className="border px-2 py-1 text-right">${enrollment.FinalCost?.toFixed(2)}</td>
                              <td className="border px-2 py-1 text-right text-green-600">
                                ${enrollment.AmountPaid?.toFixed(2)}
                              </td>
                              <td className={`border px-2 py-1 text-right font-medium ${
                                enrollment.BalanceRemaining > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                ${enrollment.BalanceRemaining?.toFixed(2)}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    {includeFinancials && (
                      <tfoot>
                        <tr className="totals-row bg-gray-100 font-semibold">
                          <td colSpan="4" className="border px-2 py-1 text-right">Subtotal:</td>
                          <td className="border px-2 py-1 text-right">
                            ${actEnrollments.reduce((sum, e) => sum + (e.FinalCost || 0), 0).toFixed(2)}
                          </td>
                          <td className="border px-2 py-1 text-right text-green-600">
                            ${actEnrollments.reduce((sum, e) => sum + (e.AmountPaid || 0), 0).toFixed(2)}
                          </td>
                          <td className="border px-2 py-1 text-right text-red-600">
                            ${actEnrollments.reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              ))
            )}

            {/* Grand Total */}
            {includeFinancials && enrollmentsByActivity.length > 1 && (
              <div className="mt-6 pt-4 border-t-2 border-gray-800">
                <table className="w-full text-sm">
                  <tr className="font-bold text-base">
                    <td className="py-2">GRAND TOTAL ({totals.students} students)</td>
                    <td className="py-2 text-right">${totals.revenue.toFixed(2)}</td>
                    <td className="py-2 text-right text-green-600 w-24">${totals.collected.toFixed(2)}</td>
                    <td className="py-2 text-right text-red-600 w-24">${totals.outstanding.toFixed(2)}</td>
                  </tr>
                </table>
              </div>
            )}

            {/* Footer */}
            <div className="footer mt-8 pt-4 border-t text-xs text-gray-500 flex justify-between">
              <span>Generated on: {new Date().toLocaleString()}</span>
              <span>Collège des Apôtres - School Management System</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityReportModal;
